/**
 * Translator — the single subscriber to `sdkEvents` that converts SDK events
 * into OTel signals (logs/metrics/traces) and pushes them through the
 * exporter.
 *
 * Why a single subscriber: the prior architecture had four independent
 * Better Stack subscribers (errors, cache-miss sampler, mint-recovery, auth)
 * each wired by the host app. Drift, double-emission, and missing platform
 * tagging were inevitable. One translator subscribes once per QueryClient;
 * everything else routes through it.
 *
 * Channels handled in v1:
 *   - cache:miss               → Counter morfi.cache.miss
 *   - subscription:change      → Sum morfi.nats.subscriptions.active (1 / -1)
 *   - subject:reacquire        → Gauge morfi.nats.reacquire.gap_ms
 *   - mutation:phase           → Span morfi.mutation (assembled from phase
 *                                events; emits span on `settled`)
 *   - http:request             → Span http.client.request for generated
 *                                queries and mutations
 *   - applyEvent:error         → Log ERROR  morfi.applyevent_error
 *   - onSignal:error           → Log ERROR  morfi.onsignal_error
 *   - observer:quarantined     → Log WARN   morfi.observer.quarantined
 *   - error                    → Log (severity per category) morfi.error
 *                                (also detects auth-telemetry's
 *                                 BetterStackAuthAction shape and routes it
 *                                 as morfi.auth.mint)
 *   - recovery                 → Log INFO   morfi.recovery
 *   - blockmeta:status         → Log INFO/WARN morfi.blockmeta
 *   - connectivity:change      → Log + Sum morfi.connectivity.transition;
 *                                ALSO drives the mint-failed→CONNECTED latch
 *                                that emits morfi.connectivity.mint_recovered
 *   - churn:subscript-thrash   → Log WARN   morfi.churn.thrash
 *   - subscription:never-bound → Log WARN   morfi.viewport.unbound (1% sample
 *                                applied by sampling.ts in prod)
 *
 * Out of scope for v1 (deferred to follow-ups, called out in TELEMETRY.md):
 *   - WS-count alarm gauge (lives on a separate mitt bus inside
 *     `src/devtool/alarms/ws-count-alarm.ts`; not yet wired to sdkEvents).
 *   - Sub-frame-sent gap histogram from `subscription-gap.ts` (uses its own
 *     sink shape; will be migrated when the SubscriptionGapSink callers are
 *     refactored to publish via sdkEvents).
 */

import type { ConnTransition } from '../connectivity/fsm/state-machine.js';
import { sdkEvents } from '../core/events.js';
import {
  AggregationTemporality,
  attrs,
  msToNs,
  newSpanId,
  newTraceId,
  nowNs,
  type OtlpKeyValue,
  type OtlpLogRecord,
  type OtlpMetric,
  type OtlpResource,
  type OtlpSpan,
  type OtlpSpanEvent,
  SeverityNumber,
  SpanKind,
  StatusCode,
} from './otel-types.js';
import type { OtlpJsonExporter } from './otlp-json-exporter.js';
import { scrubAttributes, scrubForSink } from './pii-scrub.js';
import { shouldSample } from './sampling.js';

export interface TranslatorConfig {
  /**
   * Optional callback the translator invokes ONCE per emission to fetch
   * host-supplied per-emit attributes (user.id, session.id, app.route).
   * Must be cheap — runs on every signal.
   */
  readonly enrich?: () => Readonly<Record<string, string | number | boolean | undefined>>;
  /** Override trace sample rate (default: 1.0). 0 disables traces entirely. */
  readonly traceSampleRate?: number;
}

export function installTranslator(
  exporter: OtlpJsonExporter,
  resource: OtlpResource,
  config: TranslatorConfig = {},
): () => void {
  const traceSampleRate = config.traceSampleRate ?? 1.0;

  // Mutation span tracker — keyed by `<specName>|<mutationKeyHash>` since the
  // mutation:phase event shape doesn't carry a stable mutationId. Concurrent
  // mutations to the same key collapse onto one span, which is acceptable for
  // v1 — real-world double-fires on the same key are rare and the cost of
  // adding a mutationId field everywhere isn't justified yet.
  interface MutationSpanState {
    readonly traceId: string;
    readonly spanId: string;
    readonly startTimeUnixNano: string;
    readonly specName: string;
    readonly events: OtlpSpanEvent[];
    errored: boolean;
  }
  const mutationSpans = new Map<string, MutationSpanState>();

  // Degraded-state dwell tracker. Subsumes installMintRecoveryEmitter and
  // generalises to ANY DEGRADED_* → CONNECTED traversal. On entry to a
  // DEGRADED_* state we capture (cause, enteredAt); on exit we emit a
  // `morfi.connectivity.degraded_dwell_ms` Gauge so monitors can p95 the
  // dwell time and alert. Mint-failed recovery also gets its own log line.
  let degradedSince: { cause: string | undefined; enteredAt: number } | null = null;

  // ── Helpers ─────────────────────────────────────────────────────────────

  function enrichedAttrs(
    base: Readonly<Record<string, string | number | boolean | undefined>>,
  ): ReadonlyArray<OtlpKeyValue> {
    const enriched = config.enrich?.() ?? {};
    const merged = { ...base, ...enriched };
    return attrs(scrubAttributes(merged));
  }

  function emitLog(
    name: string,
    severity: (typeof SeverityNumber)[keyof typeof SeverityNumber],
    severityText: string,
    bodyText: string,
    attrRecord: Readonly<Record<string, string | number | boolean | undefined>>,
  ): void {
    const baseAttrs = enrichedAttrs({ 'event.name': name, ...attrRecord });
    if (!shouldSample(name, baseAttrs)) return;
    const record: OtlpLogRecord = {
      timeUnixNano: nowNs(),
      severityNumber: severity,
      severityText,
      body: { stringValue: bodyText },
      attributes: baseAttrs,
    };
    exporter.exportLog(record, resource);
  }

  function emitCounter(
    name: string,
    value: number,
    attrRecord: Readonly<Record<string, string | number | boolean | undefined>>,
    isMonotonic = true,
  ): void {
    const baseAttrs = enrichedAttrs(attrRecord);
    if (!shouldSample(name, baseAttrs)) return;
    const t = nowNs();
    const metric: OtlpMetric = {
      name,
      sum: {
        aggregationTemporality: AggregationTemporality.DELTA,
        isMonotonic,
        dataPoints: [
          {
            attributes: baseAttrs,
            startTimeUnixNano: t,
            timeUnixNano: t,
            asInt: String(value),
          },
        ],
      },
    };
    exporter.exportMetric(metric, resource);
  }

  function emitGauge(
    name: string,
    value: number,
    attrRecord: Readonly<Record<string, string | number | boolean | undefined>>,
  ): void {
    const baseAttrs = enrichedAttrs(attrRecord);
    if (!shouldSample(name, baseAttrs)) return;
    const metric: OtlpMetric = {
      name,
      gauge: {
        dataPoints: [
          {
            attributes: baseAttrs,
            startTimeUnixNano: nowNs(),
            timeUnixNano: nowNs(),
            asDouble: value,
          },
        ],
      },
    };
    exporter.exportMetric(metric, resource);
  }

  // ── Subscriptions ───────────────────────────────────────────────────────

  const offCacheMiss = sdkEvents.on('cache:miss', (raw) => {
    const p = (raw ?? {}) as {
      cause?: string;
      queryKey?: ReadonlyArray<unknown>;
      correlationId?: string;
    };
    const payload: { queryKey?: ReadonlyArray<unknown>; correlationId?: string } = {};
    if (p.queryKey !== undefined) payload.queryKey = p.queryKey;
    if (p.correlationId !== undefined) payload.correlationId = p.correlationId;
    const scrubbed = scrubForSink(payload, 'better-stack');
    emitCounter('morfi.cache.miss', 1, {
      'morfi.cache.cause': p.cause ?? 'unknown',
      'morfi.query_key_hash': JSON.stringify(scrubbed.queryKey ?? []),
      'morfi.correlation_id': p.correlationId,
    });
  });

  const offSubChange = sdkEvents.on('subscription:change', (raw) => {
    const p = (raw ?? {}) as {
      subject?: string;
      direction?: 'sub' | 'unsub';
      cause?: string;
      refcount?: number;
    };
    const delta = p.direction === 'sub' ? 1 : -1;
    emitCounter(
      'morfi.nats.subscriptions.active',
      delta,
      {
        'morfi.subject': p.subject ?? '',
        'morfi.cause': p.cause ?? 'unknown',
        'morfi.direction': p.direction ?? 'unknown',
      },
      false, // non-monotonic (UpDownCounter semantics)
    );
  });

  const offReacquire = sdkEvents.on('subject:reacquire', (raw) => {
    const p = (raw ?? {}) as { subject?: string; lastSeq?: number; gapMs?: number };
    emitGauge('morfi.nats.reacquire.gap_ms', p.gapMs ?? 0, {
      'morfi.subject': p.subject ?? '',
      'morfi.last_seq': p.lastSeq,
    });
  });

  const offMutationPhase = sdkEvents.on('mutation:phase', (raw) => {
    const p = (raw ?? {}) as {
      specName?: string;
      phase?: string;
      mutationKey?: ReadonlyArray<unknown>;
      durationMs?: number;
    };
    if (p.specName === undefined || p.phase === undefined) return;
    if (traceSampleRate <= 0) return;
    const mutationId = `${p.specName}|${JSON.stringify(p.mutationKey ?? [])}`;

    if (p.phase === 'optimistic') {
      // Trace-sample decision pinned at span START.
      if (Math.random() >= traceSampleRate) return;
      mutationSpans.set(mutationId, {
        traceId: newTraceId(),
        spanId: newSpanId(),
        startTimeUnixNano: nowNs(),
        specName: p.specName,
        events: [{ timeUnixNano: nowNs(), name: 'optimistic' }],
        errored: false,
      });
      return;
    }

    const span = mutationSpans.get(mutationId);
    if (span === undefined) return; // wasn't sampled at start; ignore continuation

    span.events.push({
      timeUnixNano: nowNs(),
      name: p.phase,
      ...(p.durationMs !== undefined
        ? { attributes: attrs({ 'morfi.phase_duration_ms': p.durationMs }) }
        : {}),
    });
    if (p.phase === 'error') span.errored = true;

    if (p.phase === 'settled') {
      const endTimeUnixNano = nowNs();
      const otlpSpan: OtlpSpan = {
        traceId: span.traceId,
        spanId: span.spanId,
        name: 'morfi.mutation',
        kind: SpanKind.CLIENT,
        startTimeUnixNano: span.startTimeUnixNano,
        endTimeUnixNano,
        attributes: enrichedAttrs({
          'morfi.spec_name': span.specName,
        }),
        events: span.events,
        status: {
          code: span.errored ? StatusCode.ERROR : StatusCode.OK,
        },
      };
      exporter.exportSpan(otlpSpan, resource);
      mutationSpans.delete(mutationId);
    }
  });

  const offHttpRequest = sdkEvents.on('http:request', (raw) => {
    const p = (raw ?? {}) as {
      method?: string;
      operationName?: string;
      routeTemplate?: string;
      requestKind?: string;
      outcome?: string;
      retryCount?: number;
      statusCode?: number;
      durationMs?: number;
      correlationId?: string;
    };
    if (traceSampleRate <= 0) return;
    // Keep every failure for operational alerting; sample routine successes.
    if (p.outcome === 'success' && Math.random() >= traceSampleRate) return;
    const endMs = Date.now();
    const startMs = endMs - (p.durationMs ?? 0);
    const otlpSpan: OtlpSpan = {
      traceId: newTraceId(),
      spanId: newSpanId(),
      name: 'http.client.request',
      kind: SpanKind.CLIENT,
      startTimeUnixNano: msToNs(startMs),
      endTimeUnixNano: msToNs(endMs),
      attributes: enrichedAttrs({
        'http.request.method': p.method ?? 'GET',
        'http.route': p.routeTemplate ?? 'unknown',
        'http.response.status_code': p.statusCode,
        'morfi.operation': p.operationName,
        'morfi.request_kind': p.requestKind,
        'morfi.outcome': p.outcome,
        'morfi.retry_count': p.retryCount ?? 0,
        'morfi.correlation_id': p.correlationId,
        'morfi.dependency': 'backend_api',
      }),
      status: {
        code:
          p.outcome === 'success'
            ? StatusCode.OK
            : p.outcome === 'cancelled'
              ? StatusCode.UNSET
              : StatusCode.ERROR,
      },
    };
    exporter.exportSpan(otlpSpan, resource);
  });

  const offApplyEventError = sdkEvents.on('applyEvent:error', (raw) => {
    const p = (raw ?? {}) as { subject?: string; message?: string };
    emitLog(
      'morfi.applyevent_error',
      SeverityNumber.ERROR,
      'ERROR',
      p.message ?? 'applyEvent threw',
      {
        'morfi.subject': p.subject ?? '',
        'error.type': 'applyEvent',
        'error.message': p.message ?? '',
      },
    );
  });

  const offOnSignalError = sdkEvents.on('onSignal:error', (raw) => {
    const p = (raw ?? {}) as { subject?: string; message?: string };
    emitLog('morfi.onsignal_error', SeverityNumber.ERROR, 'ERROR', p.message ?? 'onSignal threw', {
      'morfi.subject': p.subject ?? '',
      'error.type': 'onSignal',
      'error.message': p.message ?? '',
    });
  });

  const offCacheDiagnostic = sdkEvents.on('cache:diagnostic', (raw) => {
    const diagnostic = (raw ?? {}) as { kind?: string; reason?: string; count?: number };
    emitLog(
      'morfi.cache.unavailable',
      SeverityNumber.WARN,
      'WARN',
      'Cache-dependent work is unavailable',
      {
        'morfi.cache.kind': diagnostic.kind,
        'morfi.reason': diagnostic.reason,
        'morfi.count': diagnostic.count,
      },
    );
  });

  const offProjectionDiagnostic = sdkEvents.on('conviction-projector:diagnostic', (raw) => {
    const diagnostic = (raw ?? {}) as { entityHash?: string; reason?: string };
    emitLog(
      'morfi.conviction_projection.invalid_snapshot',
      SeverityNumber.WARN,
      'WARN',
      'Conviction projection held at the last observed value',
      {
        'morfi.entity_hash': diagnostic.entityHash,
        'morfi.reason': diagnostic.reason,
      },
    );
  });

  const offResolutionThresholdDiagnostic = sdkEvents.on(
    'resolution-threshold:diagnostic',
    (raw) => {
      const diagnostic = (raw ?? {}) as { entityHash?: string; reason?: string };
      emitLog(
        'morfi.resolution_threshold.invalid_policy',
        SeverityNumber.WARN,
        'WARN',
        'Resolution threshold policy is unavailable',
        {
          'morfi.entity_hash': diagnostic.entityHash,
          'morfi.reason': diagnostic.reason,
        },
      );
    },
  );

  const offObserverQuarantined = sdkEvents.on('observer:quarantined', (raw) => {
    const p = (raw ?? {}) as { subject?: string; errors?: number };
    emitLog(
      'morfi.observer.quarantined',
      SeverityNumber.WARN,
      'WARN',
      'observer quarantined after repeated throws',
      {
        'morfi.subject': p.subject ?? '',
        'morfi.error_count': p.errors,
      },
    );
  });

  const offError = sdkEvents.on('error', (raw) => {
    if (raw === null || typeof raw !== 'object') return;
    const obj = raw as Record<string, unknown>;

    // Shape-detect: auth-telemetry's BetterStackAuthAction has a `logger`
    // field instead of an SdkError's `category`. Route those separately
    // so the migration is structurally clean.
    if (obj.logger === 'sdk.auth.mint') {
      const level = String(obj.level ?? 'info');
      const severity =
        level === 'error'
          ? SeverityNumber.ERROR
          : level === 'warn'
            ? SeverityNumber.WARN
            : SeverityNumber.INFO;
      emitLog('morfi.auth.mint', severity, level.toUpperCase(), 'auth mint event', {
        'morfi.auth.kind': asString(obj.kind),
        'morfi.auth.outcome': asString(obj.outcome),
        'morfi.auth.attempt': asNumber(obj.attempt),
        'morfi.auth.code': asString(obj.code),
        'morfi.auth.duration_ms': asNumber(obj.durationMs),
        'morfi.correlation_id': asString(obj.correlationId),
      });
      return;
    }

    // SdkError shape — category + code mandatory.
    const category = asString(obj.category);
    const code = asString(obj.code);
    if (category === undefined || code === undefined) return;

    const severity = sdkErrorSeverity(category);
    emitLog(
      'morfi.error',
      severity,
      severityToText(severity),
      asString(obj.devMessage) ?? asString(obj.userMessage) ?? code,
      {
        'morfi.error.category': category,
        'morfi.error.code': code,
        'error.type': category,
        'error.message': asString(obj.devMessage) ?? '',
        'morfi.correlation_id': asString(obj.correlationId),
        'http.response.status_code': asNumber(obj.httpStatus),
        'morfi.retry_after_ms': asNumber(obj.retryAfterMs),
        'morfi.path': asString(obj.path),
        'morfi.subject': asString(obj.subject),
      },
    );
  });

  const offRecovery = sdkEvents.on('recovery', (raw) => {
    const p = (raw ?? {}) as Record<string, unknown>;
    emitLog('morfi.recovery', SeverityNumber.INFO, 'INFO', 'recovered after error', {
      'morfi.correlation_id': asString(p.correlationId),
      'morfi.error.category': asString(p.category),
      'morfi.error.code': asString(p.code),
      'morfi.stage': asString(p.stage),
      'morfi.attempts': asNumber(p.attempts),
      'morfi.downtime_ms': asNumber(p.downtimeMs),
      'morfi.subject': asString(p.subject),
    });
  });

  const offBlockmetaStatus = sdkEvents.on('blockmeta:status', (raw) => {
    if (raw === null || typeof raw !== 'object') return;
    const obj = raw as Record<string, unknown>;
    const level = asString(obj.level) === 'warn' ? 'warn' : 'info';
    const severity = level === 'warn' ? SeverityNumber.WARN : SeverityNumber.INFO;
    emitLog(
      'morfi.blockmeta',
      severity,
      severityToText(severity),
      asString(obj.message) ?? 'blockchain.blockmeta.status',
      {
        'morfi.subject': asString(obj.subject),
        'morfi.blockmeta.mode': asString(obj.mode),
        'morfi.blockmeta.reason': asString(obj.reason),
        'error.message': asString(obj.err),
      },
    );
  });

  const offConnectivity = sdkEvents.on('connectivity:change', (raw) => {
    const p = raw as ConnTransition;
    // Degraded-dwell tracking. Captures (cause, enteredAt) on entry to any
    // DEGRADED_* state; emits dwell gauge + log on first non-degraded
    // transition. Generalises the legacy `installMintRecoveryEmitter`
    // (which only handled mint-failed) so the YAML monitor can alert on
    // p95 dwell across any cause.
    const entersDegraded = p.to === 'DEGRADED_NO_NATS' || p.to === 'DEGRADED_NO_REST';
    const leavesDegraded = p.from.startsWith('DEGRADED_') && !p.to.startsWith('DEGRADED_');

    if (entersDegraded && degradedSince === null) {
      degradedSince = { cause: p.cause, enteredAt: p.observedAt };
    } else if (leavesDegraded && degradedSince !== null) {
      const dwellMs = p.observedAt - degradedSince.enteredAt;
      const cause = degradedSince.cause ?? 'unknown';
      emitGauge('morfi.connectivity.degraded_dwell_ms', dwellMs, {
        'morfi.cause': cause,
        'morfi.connectivity.from': p.from,
        'morfi.connectivity.to': p.to,
      });
      // Special-case mint-failed for log readability (legacy event-name
      // preserved so existing dashboards searching for the phrase keep
      // matching during cutover).
      if (cause === 'mint-failed') {
        emitLog(
          'morfi.connectivity.mint_recovered',
          SeverityNumber.INFO,
          'INFO',
          'recovered from mint-failed degradation',
          {
            'morfi.connectivity.dwell_ms': dwellMs,
            'morfi.connectivity.from': p.from,
            'morfi.connectivity.to': p.to,
          },
        );
      }
      degradedSince = null;
    }

    // Always emit the transition log + counter.
    emitLog(
      'morfi.connectivity.transition',
      p.to === 'OFFLINE' || p.to === 'DEGRADED_NO_NATS' ? SeverityNumber.WARN : SeverityNumber.INFO,
      p.to === 'OFFLINE' || p.to === 'DEGRADED_NO_NATS' ? 'WARN' : 'INFO',
      `connectivity ${p.from} → ${p.to}`,
      {
        'morfi.connectivity.from': p.from,
        'morfi.connectivity.to': p.to,
        'morfi.connectivity.cause': p.cause,
      },
    );
    emitCounter('morfi.connectivity.transitions', 1, {
      'morfi.connectivity.from': p.from,
      'morfi.connectivity.to': p.to,
      'morfi.connectivity.cause': p.cause,
    });
  });

  const offChurn = sdkEvents.on('churn:subscript-thrash', (raw) => {
    const p = (raw ?? {}) as {
      specName?: string;
      queryKeyHash?: string;
      dwellMs?: number;
    };
    emitLog('morfi.churn.thrash', SeverityNumber.WARN, 'WARN', 'rapid sub/unsub cycles detected', {
      'morfi.spec_name': p.specName,
      'morfi.query_key_hash': p.queryKeyHash,
      'morfi.churn.dwell_ms': p.dwellMs,
    });
  });

  const offNeverBound = sdkEvents.on('subscription:never-bound', (raw) => {
    const p = (raw ?? {}) as { specName?: string; queryKey?: ReadonlyArray<unknown> };
    emitLog(
      'morfi.viewport.unbound',
      SeverityNumber.WARN,
      'WARN',
      'viewport-gated hook never bound an element',
      {
        'morfi.spec_name': p.specName,
        'morfi.query_key_hash': JSON.stringify(p.queryKey ?? []),
      },
    );
  });

  const offHandoffRecovery = sdkEvents.on('realtime:handoff-recovery', (raw) => {
    const p = (raw ?? {}) as { specName?: string; reason?: string };
    emitLog(
      'morfi.realtime.handoff_recovery',
      SeverityNumber.WARN,
      'WARN',
      'realtime snapshot handoff requires authoritative recovery',
      {
        'morfi.spec_name': p.specName,
        'morfi.handoff.reason': p.reason,
      },
    );
  });

  // ── Dispose ─────────────────────────────────────────────────────────────

  const offs = [
    offCacheMiss,
    offSubChange,
    offReacquire,
    offMutationPhase,
    offHttpRequest,
    offApplyEventError,
    offOnSignalError,
    offProjectionDiagnostic,
    offResolutionThresholdDiagnostic,
    offCacheDiagnostic,
    offObserverQuarantined,
    offError,
    offRecovery,
    offBlockmetaStatus,
    offConnectivity,
    offChurn,
    offNeverBound,
    offHandoffRecovery,
  ];

  return (): void => {
    for (const off of offs) {
      try {
        off();
      } catch {
        /* never throw from teardown */
      }
    }
    mutationSpans.clear();
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function sdkErrorSeverity(category: string): (typeof SeverityNumber)[keyof typeof SeverityNumber] {
  switch (category) {
    case 'schema':
    case 'auth':
    case 'server':
      return SeverityNumber.ERROR;
    case 'rate-limited':
    case 'precondition_failed':
    case 'validation':
      return SeverityNumber.WARN;
    case 'network':
    case 'realtime-degraded':
      return SeverityNumber.WARN;
    default:
      return SeverityNumber.ERROR;
  }
}

function severityToText(sev: (typeof SeverityNumber)[keyof typeof SeverityNumber]): string {
  if (sev === SeverityNumber.ERROR) return 'ERROR';
  if (sev === SeverityNumber.WARN) return 'WARN';
  if (sev === SeverityNumber.INFO) return 'INFO';
  if (sev === SeverityNumber.DEBUG) return 'DEBUG';
  if (sev === SeverityNumber.FATAL) return 'FATAL';
  return 'TRACE';
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function asNumber(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
